import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useApi } from "@/core/api";
import { CONVERSATION_TYPES } from "./constants";
import { IMostRecentTextMessage, ITextMessage } from "./text-messages";

export const businessTextMessagesKey = (businessId?: string | null) =>
  ["business-text-messages", businessId] as const;
export const customerTextMessagesKey = (customerId?: string | null) =>
  ["customer-text-messages", customerId] as const;

export const customerTextMessagesQueryOptions = (
  get: ReturnType<typeof useApi>["get"],
  customerId?: string | null,
) => ({
  queryKey: customerTextMessagesKey(customerId),
  queryFn: () =>
    get(`/customers/${customerId}/text_messages`).then((res) => res.data),
});

export const usePrefetchCustomerTextMessages = () => {
  const { get } = useApi();
  const queryClient = useQueryClient();

  return useCallback(
    (customerId: string) => {
      void queryClient.prefetchQuery(
        customerTextMessagesQueryOptions(get, customerId),
      );
    },
    [get, queryClient],
  );
};

export const useGetBusinessTextMessages = <TData = IMostRecentTextMessage[]>(
  businessId?: string | null,
  select?: (data: IMostRecentTextMessage[]) => TData,
) => {
  const { get } = useApi();

  return useQuery({
    queryKey: businessTextMessagesKey(businessId),
    queryFn: async (): Promise<IMostRecentTextMessage[]> =>
      get(`/businesses/${businessId}/text_messages`).then((res) => res.data),
    enabled: !!businessId,
    select,
  });
};

export const useGetInboundTextMessages = (businessId?: string | null) =>
  useGetBusinessTextMessages(
    businessId,
    useCallback(
      (data: IMostRecentTextMessage[]) =>
        data.filter(
          (message) =>
            message.conversation_type === CONVERSATION_TYPES.conversation,
        ),
      [],
    ),
  );

export const useGetOutboundTextMessages = (businessId?: string | null) =>
  useGetBusinessTextMessages(
    businessId,
    useCallback(
      (data: IMostRecentTextMessage[]) =>
        data.filter(
          (message) =>
            message.conversation_type === CONVERSATION_TYPES.outbound ||
            message.conversation_type === CONVERSATION_TYPES.manual,
        ),
      [],
    ),
  );

export const useGetOptedOutTextMessages = (businessId?: string | null) =>
  useGetBusinessTextMessages(
    businessId,
    useCallback(
      (data: IMostRecentTextMessage[]) =>
        data.filter(
          (message) =>
            message.conversation_type === CONVERSATION_TYPES.opted_out,
        ),
      [],
    ),
  );

export const useGetTextMessage = (
  businessId?: string | null,
  textMessageId?: string | null,
) =>
  useGetBusinessTextMessages(
    businessId,
    useCallback(
      (messages: IMostRecentTextMessage[]) =>
        messages.find((m) => m.id === textMessageId) || null,
      [textMessageId],
    ),
  );

export const useGetCustomerTextMessages = (customerId?: string | null) => {
  const { get } = useApi();

  return useQuery<ITextMessage[]>({
    ...customerTextMessagesQueryOptions(get, customerId),
    enabled: !!customerId,
  });
};
